import React, {useCallback} from 'react';

import {useEvent, useStore} from 'effector-react';
import {Controller, Message, ValidationRule, Validate} from 'react-hook-form';

import {DatePicker} from 'components/ui-kit/DatePicker';
import {DatePickerRanged} from 'components/ui-kit/DatePickerRanged';

import {Input} from 'components/ui-kit/Input';
import {AsyncSelect} from 'components/ui-kit/Select';

import {Switch} from 'components/ui-kit/Switcher';
import {$allowedActionsForInvoice} from 'models/documents/invoices/common';
import {$infoValues, updateCustomerCompanyNameOrTIN} from 'models/documents/invoices/create';

import {CustomRadioGroup} from '../SimpleRadio';

type Rules =
    | Partial<{
          required: Message | ValidationRule<boolean>;
          min: ValidationRule<number | string>;
          max: ValidationRule<number | string>;
          maxLength: ValidationRule<number | string>;
          minLength: ValidationRule<number | string>;
          pattern: ValidationRule<RegExp>;
          validate: Validate | Record<string, Validate>;
          valueAsNumber: boolean;
          valueAsDate: boolean;
          setValueAs: (value: any) => any;
      }>
    | undefined;

export const UniController = ({
    item,
    control,
    setValue,
    errors,
    index,
    trigger,
    cb,
    rules,
}: {
    item: Record<string, any>;
    control: any;
    setValue?: any;
    errors?: any;
    index?: number;
    trigger?: any;
    cb?: (x: {
        name: string;
        value: unknown;
        postalCode?: string;
        country?: string;
        city?: string;
        street?: string;
    }) => void;
    rules?: Rules;
}) => {
    const {
        type,
        rawType,
        numberSteps,
        id,
        options,
        disabled,
        label,
        placeholder,
        name,
        required,
        defaultVal,
        selectMenuPlacement,
        isSearchable,
    } = item;

    const infoValueStore = useStore($infoValues);
    const updateCustomerCompanyName = useEvent(updateCustomerCompanyNameOrTIN);
    const allowedActionsForInvoice = useStore($allowedActionsForInvoice);
    const handleRadioChange = (value: string, name: string) => {
        setValue(name, value);
    };

    function updateSelectsValues(select: Record<string, string>[]) {
        return select.map((i: Record<string, string>) => ({
            ...i,
            value: i.taxIdNumber,
            label: i.legalName,
        }));
    }

    const loadSelectValues = useCallback(async (inputValue: string) => {
        if (!inputValue) {
            const valuesArr = await item.fetchOptions();

            return name === 'customerCompanyNameOrTIN' ? updateSelectsValues(valuesArr) : valuesArr;
        }
        const props = {
            combinedSearch: inputValue,
        };
        const valueFromSearch = await item.fetchOptions(props);
        return name === 'customerCompanyNameOrTIN'
            ? updateSelectsValues(valueFromSearch)
            : valueFromSearch;
    }, []);

    const loadStorageNames = useCallback(
        async (inputValue: string) => {
            const tin = (infoValueStore.customerCompanyNameOrTIN as {value: string; label: string})
                ?.value;
            if (!tin) return [];
            const cities = await item.fetchOptions(tin);
            if (!inputValue)
                return cities.map((i: Record<string, string>) => ({
                    value: i.address ?? '-',
                    label: i.storageName ?? '-',
                }));
            return cities.map((i: Record<string, string>) => ({
                value: i.address ?? '-',
                label: i.storageName ?? '-',
            }));
        },
        [infoValueStore.customerCompanyNameOrTIN],
    );

    return (
        <div key={id ?? `${name}_${index}`}>
            <Controller
                control={control}
                name={name}
                defaultValue={defaultVal}
                rules={{...rules, required}}
                render={(props) => {
                    switch (type) {
                        case 'input': {
                            const handleErrorsFromFields = () => {
                                if ((name as string).startsWith('charges[')) {
                                    return errors['charges']?.[index as number];
                                }
                                if (name.startsWith('calculationsByTaxCategory[')) {
                                    const nameInArray = name.split('].')[1];
                                    const hasErrors =
                                        errors['calculationsByTaxCategory']?.[index as number]?.[
                                            nameInArray
                                        ];
                                    return hasErrors ?? false;
                                }
                                return name in errors;
                            };
                            return (
                                <Input
                                    {...props}
                                    type={rawType ?? 'text'}
                                    step={numberSteps}
                                    isRequired={required}
                                    placeholder={!allowedActionsForInvoice.edit ? '-' : placeholder}
                                    label={label}
                                    disabled={disabled}
                                    hasError={errors && handleErrorsFromFields()}
                                    onChange={(val) => {
                                        props.onChange(val);
                                        cb &&
                                            cb({
                                                name,
                                                value: val.target.value,
                                            });
                                    }}
                                />
                            );
                        }

                        case 'select': {
                            if (name === 'storageLocationName') {
                                return (
                                    <AsyncSelect
                                        {...props}
                                        key={
                                            JSON.stringify(
                                                infoValueStore.customerCompanyNameOrTIN,
                                            ) || 'no-storage'
                                        }
                                        label={label}
                                        isRequired={required}
                                        isSearchable={false}
                                        cacheOptions
                                        defaultOptions
                                        disabled={
                                            disabled ||
                                            !(infoValueStore.customerCompanyNameOrTIN as {
                                                value: string;
                                                label: string;
                                            })?.value
                                        }
                                        loadOptions={loadStorageNames}
                                        onChange={(val: {value: string; label: string}) => {
                                            if (!val) {
                                                return;
                                            }
                                            // setValue(name, val);
                                            props.onChange(val);

                                            name in errors && trigger && trigger(name);

                                            if (cb) {
                                                cb({
                                                    name,
                                                    value: val.value,
                                                });
                                            }
                                        }}
                                        hasError={name in errors}
                                    />
                                );
                            }
                            return (
                                <AsyncSelect
                                    {...props}
                                    label={label}
                                    isRequired={required}
                                    isSearchable={isSearchable ?? true}
                                    defaultOptions
                                    disabled={disabled}
                                    menuPlacement={selectMenuPlacement}
                                    loadOptions={loadSelectValues}
                                    optionComponent={item.optionComponent ?? undefined}
                                    placeholder={!allowedActionsForInvoice.edit ? '-' : placeholder}
                                    onChange={(val: {
                                        label: string;
                                        value: string;
                                        postalCode?: string;
                                        city?: string;
                                        country?: string;
                                        street?: string;
                                    }) => {
                                        if (!val) {
                                            return;
                                        }
                                        if (name === 'customerCompanyNameOrTIN') {
                                            updateCustomerCompanyName(val);
                                        }

                                        // setValue(name, val);
                                        props.onChange(val);

                                        name in errors && trigger && trigger(name);

                                        if (cb) {
                                            cb({
                                                name,
                                                value: val.value,
                                                ...(name === 'customerCompanyNameOrTIN' && {
                                                    postalCode: val.postalCode,
                                                    city: val.city ?? '',
                                                    country: val.country ?? '',
                                                    street: val.street ?? '',
                                                }),
                                            });
                                        }
                                    }}
                                    hasError={name in errors}
                                />
                            );
                        }

                        case 'radio': {
                            const {ref, value, ...rest} = props;
                            return (
                                <CustomRadioGroup
                                    {...rest}
                                    // defaultVal={props.value}
                                    value={value}
                                    disabled={disabled}
                                    options={options}
                                    name={props.name}
                                    onChange={(v) => {
                                        // handleRadioChange(v, props.name);
                                        props.onChange(v);
                                    }}
                                />
                            );
                        }

                        case 'switcher': {
                            const {ref, ...rest} = props;
                            return (
                                <Switch
                                    {...rest}
                                    disabled={disabled}
                                    name={props.name}
                                    label={label}
                                    defaultVal={defaultVal}
                                    onChange={(value) => {
                                        props.onChange(value);
                                        cb &&
                                            cb({
                                                name,
                                                value,
                                            });
                                    }}
                                />
                            );
                        }

                        case 'date': {
                            const {ref, ...rest} = props;
                            return (
                                <DatePicker
                                    {...rest}
                                    isRequired={required}
                                    placeholderText={
                                        !allowedActionsForInvoice.edit ? '-' : placeholder
                                    }
                                    selected={props.value}
                                    disabled={disabled}
                                    label={label}
                                    hasError={name in errors}
                                />
                            );
                        }

                        case 'date-range': {
                            const {ref, ...rest} = props;
                            return (
                                <DatePickerRanged
                                    {...rest}
                                    disabled={disabled}
                                    placeholder={!allowedActionsForInvoice.edit ? '-' : placeholder}
                                    label={label}
                                />
                            );
                        }

                        default:
                            return <></>;
                    }
                }}
            />
        </div>
    );
};
